import type {UuidDTO} from '@/ts/types/dto/sign-in-dto';
import type {MoodDTO} from '@/ts/types/dto/journals-dto';

/**
 * View-model types for the redesigned Today page (IPD-2903 epic).
 *
 * These are COMPONENT-facing types: tiles and the bento grid consume them exclusively.
 * The backend payload contract for GET /me/today is settled (see the header of `dto/today-dto.ts`).
 * Where a tile consumes it, a pure mapper translates the DTO into these types so contract changes
 * never reach the components: today that is the upcoming and tasks mappers. The journal and program
 * tiles predate the payload and still read their own endpoints through stateful composables.
 */

import type {RouteLocationRaw} from 'vue-router';
import type {LibraryMediaTypeDTO} from '@/ts/types/dto/recco/library.dto';

/** Every tile slot the Today bento can render, in no particular order */
export enum TodayTileKind {
  upcoming = 'upcoming',
  taskList = 'taskList',
  explore = 'explore',
  contentPrimary = 'contentPrimary',
  contentSecondary = 'contentSecondary',
  assessment = 'assessment',
  journal = 'journal',
  programs = 'programs',
}

/** How many columns a tile occupies inside its row once the row has more than one column */
export type TodayTileSpan = 'narrow' | 'wide' | 'full';

/** A single cell of a bento row: which tile it hosts and how wide it wants to be */
export interface TodayTileCell {
  kind: TodayTileKind;
  span: TodayTileSpan;
}

/**
 * Identifies a band of the bento. Narrowed on purpose: the grid builds each row's accessible
 * name as `today_row_<id>`, so an unknown id would render the raw key as the section label.
 */
export type TodayRowId = 'focus' | 'discover' | 'reflect' | 'programs';

/** One horizontal band of the bento; each row lays out its cells independently */
export interface TodayRow {
  id: TodayRowId;
  cells: TodayTileCell[];
}

/**
 * One recommended piece of content, as a content tile renders it (IPD-2978 12.1).
 *
 * The two tiles hold one item each and are filled, ranked and rotated entirely by the backend
 * (IPD-2969): featured by the organization first, then the curated welcome set, then the tag-based
 * recommendation engine, refreshed as a pair every three days and looping when the pool runs out.
 * None of that is visible here — a tile is handed one item and draws it, which is why "featured",
 * "welcome" and "recommended" are not states of this type: they are how the item was chosen, and
 * they look identical.
 *
 * `media` is deliberately the Explore vocabulary rather than a Today-specific enum. The play
 * badge, the Watched / Read / Listened wording and Explore's own cards all key off
 * `LibraryMediaTypeDTO`, so a parallel enum here would mean a second copy of `isPlayableMediaType`
 * and of the label map, free to drift from the one the rest of the app derives.
 */
export interface TodayContent {
  /** Directus content id. Stable, and what the card links to. */
  id: string;
  title: string;
  media: LibraryMediaTypeDTO;
  /**
   * Length in seconds of the video or audio, for the eyebrow to print beside the medium so the
   * reader knows what they are committing to before opening it (IPD-3375). Null when the piece is
   * an article or the CMS holds no length for its file, in which case the eyebrow names the medium
   * alone.
   */
  duration: number | null;
  /** Directus asset id or absolute URL. Null leaves the card on its own dark surface. */
  imageUrl: string | null;
  /**
   * The partner logo a piece is attributed to, when it has one. Carried rather than dropped
   * because every other surface that draws this content renders it (Explore's cards and hero,
   * the home bands, and the content page this card opens), so leaving it out would attribute the
   * same piece in one place and not in another.
   *
   * Optional rather than nullable, mirroring the DTO field and the badge atom's own prop, so it
   * travels from one to the other without a coercion in between.
   */
  companyImage?: string | null;
  /**
   * The backend's existing `consumed` flag. A read item is not replaced early: it keeps its slot,
   * dimmed, until the pair refreshes, so the page does not rearrange under someone who just
   * came back from reading it.
   */
  read: boolean;
  to: RouteLocationRaw;
}

/**
 * Whether the user may answer an assessment right now, exactly as the payload states it
 * (IPD-2971 §5). The backend decides which single one is open and everything else arrives locked,
 * so the pacing is not left to each client to reinvent.
 */
export enum TodayAssessmentStatus {
  available = 'available',
  locked = 'locked',
}

/**
 * One assessment, as it arrives for the slider to draw.
 *
 * Deliberately narrower than the payload: an assessment travels as a whole questionnaire, with its
 * questions, score and citations, and none of that is drawable here. The card shows a name and a
 * picture.
 */
export interface TodayAssessmentItem {
  /** Questionnaire id. Stable, and what the CTA opens. */
  id: string;
  /** The questionnaire's own name, straight from the CMS. */
  title: string;
  status: TodayAssessmentStatus;
  /** Directus asset id or absolute URL. Null falls back to the interim glyph. */
  imageUrl: string | null;
  /**
   * An animated drawing, which has to be inlined rather than hung in an `<img>` for its hover
   * animation to be reachable. Absent on everything the CMS has no such file for, and absent on
   * everything until the backend that serves the field is deployed (IPD-3062).
   */
  artUrl?: string;
  /** The card colour the CMS chose, as a hex string. Absent means the tile picks one of its own. */
  backgroundColor?: string;
  /**
   * How long until it reopens, for one already answered. Null while it has never been answered or
   * is simply queued behind another.
   *
   * A remaining time rather than a date, because it keeps the subtraction out of every component
   * that renders one. The contract sends only the instant (`unlockDate`), and that is enough — a
   * remaining time is a duration, so no timezone can make the two sides disagree — so the mapper is
   * the single place that computes it, at map time. The ask for a backend-computed field (mirroring
   * the session's `timeUntilUnlockMillis`) was raised and then withdrawn for that reason; do not
   * re-raise it. Known trade-off: a duration computed at map time is a snapshot, and it ages if the
   * page is left open — harmless while the copy speaks in days, worth revisiting if it ever speaks
   * in minutes.
   */
  unlockInMillis: number | null;
}

/**
 * What the tile draws as a whole, which is not the same axis as what each card draws: the design
 * (IPD-2905) enumerates tile-level states while the payload states per-item availability, and one
 * single-item payload can satisfy two of the design's states at once. The face is therefore a
 * projection of the head of the list, computed once, so the tile's copy and its cards can never
 * disagree about which state they are in.
 */
export enum TodayAssessmentFace {
  /** Something to answer now: the head is open. The onboarding one included — see the resolver. */
  available = 'available',
  /**
   * Nothing open, and the next one says when it comes back. Design's state 4: the card carries its
   * own "Unlocks in XX days" and the tile puts no line above it, however many cards follow.
   */
  countdown = 'countdown',
  /**
   * Nothing open and nothing to count down to, which is the first-use state: the onboarding CTA is
   * in the Upcoming tile and everything here waits behind it. The copy must not name the onboarding
   * questionnaire (decided 2026-07-07, to avoid a second reference to it).
   */
  locked = 'locked',
}

/** What a single card of the slider draws. */
export enum TodayAssessmentSlideState {
  /** The one and only the user can act on. */
  open = 'open',
  /** Locked because another is ahead of it. Visible, not actionable, and with nothing to count. */
  queued = 'queued',
  /** Locked because it was answered, and it says when it reopens. */
  cooldown = 'cooldown',
}

/** One card of the slider. */
export interface TodayAssessmentSlide {
  id: string;
  title: string;
  imageUrl: string | null;
  /** See {@link TodayAssessmentItem.artUrl}. */
  artUrl?: string;
  /** See {@link TodayAssessmentItem.backgroundColor}. */
  backgroundColor?: string;
  state: TodayAssessmentSlideState;
  /** Only ever set on a {@link TodayAssessmentSlideState.cooldown} card. */
  unlockInMillis: number | null;
}

/** The whole tile, resolved: one face and the cards behind it. */
export interface TodayAssessmentView {
  face: TodayAssessmentFace;
  slides: TodayAssessmentSlide[];
}


/** Time-of-day band the Upcoming tile greets the user with. */
export enum TodayGreeting {
  morning = 'morning',
  afternoon = 'afternoon',
  evening = 'evening',
}

/**
 * Everything the shared card can stand for. The Figma sheet draws a recommended programme
 * with exactly the same card as a pending session, so one kind covers both — the four task
 * types plus the programme that only ever appears as a recommendation.
 */
export enum TodayCardKind {
  session = 'session',
  homework = 'homework',
  challenge = 'challenge',
  assessment = 'assessment',
  program = 'program',
  /**
   * A piece of reading, watching or listening. Only ever a recommendation: the payload offers one
   * of intervention | challenge | content on the "Awesome!" face, and content is the third. Figma
   * never drew that variant, so its tint and glyph are ours until design draws one.
   */
  content = 'content',
}

/**
 * The four kinds the task aggregation can surface (IPD-2967). "Task" is a projection, not an
 * entity: nothing in the backend is stored as one, it is what is pending across sessions,
 * homework, challenges and questionnaires. A programme is never a task — you enrol in it.
 */
export type TodayTaskType =
  | TodayCardKind.session
  | TodayCardKind.homework
  | TodayCardKind.challenge
  | TodayCardKind.assessment;

/** One actionable thing, as the shared card renders it. */
export interface TodayCardItem {
  /** Stable across reads; the aggregation is recomputed per request and never persisted. */
  id: string;
  kind: TodayCardKind;
  /** The item itself: session name, homework page name, challenge item, questionnaire name. */
  title: string;
  /**
   * Programme or challenge it belongs to, shown above the title. Null for a standalone item
   * (a questionnaire has no parent), where the card falls back to its kind label.
   */
  parentName: string | null;
  /** Directus asset id or absolute URL. Null falls back to the kind illustration. */
  imageUrl: string | null;
  /**
   * Completion as a 0–1 fraction, or null when the kind has no progress to show.
   *
   * The payload settled what the bar means, and it is not what Figma #10 asked about: a session
   * carries its own `sessionProgress` (completed pages over total) and a challenge session the
   * same over its tasks. So the bar is the progress of the item on the card, not of the
   * programme around it. Homework, assessments and recommendations have none.
   */
  progress: number | null;
  /** Already done. The list variant dims it and marks it instead of dropping it. */
  done: boolean;
  /** Where the card leads. Null for items that open in a modal, which emit `select` instead. */
  to: RouteLocationRaw | null;
}

/**
 * One task, as the Upcoming tile and the task list both render it.
 *
 * The same card appears in two places by design: the Upcoming tile shows task #1 and the
 * task-list tile shows the same list from #1 down, so the two can never disagree on copy.
 */
export interface TodayTask extends TodayCardItem {
  kind: TodayTaskType;
}

/**
 * The onboarding questionnaire the welcome CTA opens. It is the only questionnaire the tile ever
 * names, and it arrives with the face rather than being looked up: the payload sends it precisely
 * so the CTA has something to open.
 */
export interface TodayOnboardingQuestionnaire {
  id: string;
  name: string;
}

/** What the tile's unlock date belongs to, and where the reader stands in it */
export interface TodayNextTask {
  /** The programme or challenge the session belongs to */
  parentName: string;
  /**
   * What to call the session. Its own title, or its programme's when the CMS left that blank —
   * the sentence names something either way rather than trailing off into the date.
   */
  title: string;
  sessionNumber: number;
  totalSessions: number;
}

/**
 * The faces of the Upcoming tile (IPD-2976 10.1). Six, as the ticket enumerates them; Figma
 * draws five of them across ten instances, because the recommendation is shown once per
 * content type and the task once per task type.
 *
 * The frontend does not choose the face — it arrives resolved in the payload and is rendered.
 */
export enum TodayUpcomingFace {
  /** Onboarding never answered: the tile owns the single onboarding CTA. */
  welcome = 'welcome',
  /** Onboarding just answered: "Awesome!" plus one recommendation. */
  recommendation = 'recommendation',
  /** Something is due now: greeting plus the top task. */
  task = 'task',
  /** Nothing due yet, but something is coming: greeting plus the unlock date. */
  nothingDue = 'nothingDue',
  /** Nothing due and nothing coming: greeting plus a nudge towards Explore. */
  idle = 'idle',
  /**
   * A suggestion without the congratulation: greeting, a plain line, and the same card the
   * recommendation face draws.
   *
   * The backend answers "nothing to do, now or later" with a recommendation whoever the user is,
   * and `justOnboarded` says only whether they earned the "Awesome!" heading today. Everyone else
   * arriving in that state gets this face — the user who onboarded days ago and never enrolled,
   * the one between sessions whose next unlock is further out than the backend announces, and the
   * one who finished everything, which the rules doc and the state matrix both answer with a
   * recommendation. Without it the page throws the suggestion away and says "nothing on the
   * docket" to a user it was picked for.
   *
   * The matrix names the last of those S7 "All done", which is what this face was called while it
   * was unreachable and drawn nowhere. It covers more than that, hence the plainer name.
   */
  suggestion = 'suggestion',
}

/**
 * What the Upcoming tile renders, one face at a time. A discriminated union rather than a bag
 * of optional fields, so a face can never arrive without the data it needs to draw.
 */
export type TodayUpcoming =
  | {face: TodayUpcomingFace.welcome; questionnaire: TodayOnboardingQuestionnaire}
  | {face: TodayUpcomingFace.recommendation; recommendation: TodayCardItem}
  | {face: TodayUpcomingFace.task; task: TodayTask}
  | {face: TodayUpcomingFace.nothingDue; nextUnlockAt: string; nextTask: TodayNextTask}
  | {face: TodayUpcomingFace.idle}
  | {face: TodayUpcomingFace.suggestion; recommendation: TodayCardItem};

/**
 * The faces of the tasks tile (IPD-2977 11.1), the top-right slot next to Upcoming. Six as the
 * ticket enumerates them, because the three promos are one face shown with three sets of copy.
 *
 * Which one shows is decided by the backend (IPD-2970), in this precedence: intro video while
 * unwatched and within 14 days of registration → task list → all-caught-up for the rest of the
 * day the last task was completed → promo rotation. None of that is re-derived here.
 */
export enum TodayTasksFace {
  /** The "What is Balm?" intro, shown until watched (50%) or the 14-day window closes. */
  video = 'video',
  /** The overflow of the Upcoming tile: the same aggregation, capped at five. */
  taskList = 'taskList',
  /** The last task of the day is done. Holds until the calendar day ends, then promos resume. */
  allCaughtUp = 'allCaughtUp',
  /** Nothing pending: one discovery promo per day, rotated by the backend. */
  promo = 'promo',
}

/**
 * The three promos the tile rotates through. Each is a fixed piece of copy, not CMS content —
 * only which one shows on a given day comes from the backend.
 */
export enum TodayPromoKind {
  challenge = 'challenge',
  medicalProgram = 'medicalProgram',
  preventionProgram = 'preventionProgram',
}

/**
 * What the tasks tile renders, one face at a time. Discriminated like {@link TodayUpcoming}, so
 * the promo face cannot arrive without knowing which promo, nor the list without its tasks.
 */
export type TodayTasks =
  | {
    face: TodayTasksFace.video;
    /** Whether the player should start itself, muted. Decided by the backend (IPD-3114). */
    autoplay: boolean;
    /**
     * The content the video belongs to. Carried so the page can report it watched through
     * `POST /me/block-content/{contentId}/watched`, which is what closes the 14-day intro
     * window; the tile itself never calls it, and wiring the call is the integration ticket's.
     *
     * Not nullable: the face is only built when the payload carries the content, because a
     * video face that cannot say which video it is has nothing to report.
     */
    contentId: UuidDTO;
    /**
     * The video file itself, so the tile can play it where it stands rather than sending the user
     * to a page for it — which is what the design draws and what the current home already does with
     * this same piece.
     *
     * Not nullable, and the face is not built without it: a video face with no video has nothing to
     * play and nothing to send anyone to.
     */
    videoUrl: string;
    posterUrl: string | null;
  }
  | {face: TodayTasksFace.taskList; tasks: TodayTask[]}
  | {face: TodayTasksFace.allCaughtUp}
  | {face: TodayTasksFace.promo; promo: TodayPromoKind};

/**
 * How many rows the list shows. The backend already caps the overflow (IPD-2970 4.4), so this is
 * a guard against a payload that grows past the design rather than a selection rule.
 */
export const TODAY_TASK_LIST_MAX = 5;

/* ── Programs tile (IPD-3037) ───────────────────────────────────────────────── */

/**
 * Which catalogue a program belongs to. The payload's `InterventionTypeDTO` maps
 * onto this; a program the CMS never categorised arrives in neither and is left
 * out rather than guessed into a group.
 */
export type ProgramCatalogue = 'medical' | 'prevention';

/** One program row in the tile */
export interface TileProgram {
  interventionId: UuidDTO;
  name: string;
  catalogue: ProgramCatalogue;
  /** 0..1. Ignored once `completed` is true — the row shows a summary instead */
  progress: number;
  completed: boolean;
  /** Directus asset id for the row thumbnail; absent falls back to a plain disc */
  image?: UuidDTO | null;
}

/* ── Journal tile (IPD-2990) ────────────────────────────────────────────────── */

/** One card in the journal tile's fanned stack */
export interface StackEntry {
  id: string;
  mood: MoodDTO;
  /** Absent for smiley-only entries — the card then shows the face instead */
  text?: string | null;
  /**
   * When the entry was written, ISO-8601. Kept raw rather than pre-formatted so
   * the card renders it in the active language. Absent on entries the API
   * returned without a date, which then show no date line rather than dropping.
   */
  date?: string;
}

/**
 * A feeling offered in the entry form. The id is what the API stores, so it has
 * to travel with the label — a name alone cannot be persisted.
 */
export interface JournalFeelingOption {
  id: UuidDTO;
  name: string;
}

/** Draft emitted when the entry form is saved */
export interface JournalEntryDraft {
  mood: MoodDTO;
  text: string;
  /** Ids of the chosen secondary feelings, ready for the API */
  feelingIds: UuidDTO[];
}
